import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import '@ant-design/compatible/assets/index.css';

import { Row, message, Alert } from 'antd';
import {
	clearFiltersQuotes,
	fetchQuotes,
	updateFiltersQuotes,
	updatePaginationQuotes,
	updateSortingQuotes,
	replaceFiltersAndSortingQuotes,
	updateSelection,
	updateQuoteStatusBulkWrapper,
} from '../../thunks/quotes_thunks_wrapper';
import {
	getEntitiesById,
	getSelectionObject,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import TableHeader from './TableHeader';
import QuotesListTable from './QuotesListTable';
import TableFooter from './TableFooter';
import {
	getRecordsForTargetCollection,
	standardInitialStateGenerator,
} from '../../reducers/standard_reducer_utils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { unionWith, differenceWith, isEqual } from 'lodash';
import TableSelectionRow from './TableSelectionRow/TableSelectionRow';

import {
	QUOTE_MULTI_SELECT_ACTION_CONFIG_FOR_BUYER,
	QUOTE_MULTI_SELECT_ACTION_CONFIG_FOR_SUPPLIER,
} from './config/multiSelect/multiSelect';

import { isInternalTech, isUserManagedBySupplier } from '../../utils/AuthUtils';
import { downloadQuoteCSV } from '../../thunks/quotes_thunks';
import { isSubcontractorQuoteViewLocation } from '../quote_utils/QuoteUtils';
import { useFilters } from '../../hooks/useFilters';
import { getQuoteFilterConfig } from '../../utils/WorkOrderUtils';

const TARGETCOLLECTIONNAME = 'quotesTable';

function QuotesTable({
	data,
	error,
	openMoreInDefault,
	updatePagination,
	quotes,
	quoteSlice,
	fetchData,
	location,
	updateSorting,
	updateFilters,
	clearFilters,
	companyConfig,
	currentUser,
	downloadQuotesCSV,
	CustomFooter,
	CustomHeader,
	renderQuote,
	tableStyle,
	history,
	renderEmptyState,
	targetCollectionName,
	userType,
	filterConfig,
	enableViews,
	replaceFiltersAndSorting,
	preAppliedFilters,
	enableMultiSelect,
	updateSelections,
	updateQuoteStatuses,
	errors,
	enableBannerMessages,
	//loading,
	enableMessaging,
	backText,
}) {
	const {
		filters: activeFilters,
		pagination,
		sorting: activeSorters,
	} = quotes || standardInitialStateGenerator([targetCollectionName]);
	React.useEffect(() => {
		const queryParams = new URLSearchParams(location.search);
		const preserve = queryParams.has('preserve');

		if (!preserve || !(data && data.length)) {
			fetchData(location);
		}
	}, []);

	//Statuses
	const { creating, archiving, unarchiving, fetching, updating } = quoteSlice;
	if (enableMessaging) {
		updating
			? message.open({
					key: 'quoteTableMessageUpdating',
					type: 'loading',
					content: 'Updating...',
					duration: 60,
			  })
			: message.destroy('quoteTableMessageUpdating');
	}

	const quoteFilters = useFilters({
		filterTargetCollectionName: 'quotesAdvancedFilters',
		overviewCollectionName: TARGETCOLLECTIONNAME,
		updateFilters,
	});

	const FILTER_CONFIG = useMemo(() => {
		const defaultConfig = getQuoteFilterConfig({
			filters: quoteFilters,
			userType,
			currentUser,
		});
		const advancedFilterConfig =
			filterConfig && filterConfig.filters && filterConfig.filters.length > 0
				? filterConfig.filters.map((filter) => defaultConfig.find((_) => _.fieldName === filter))
				: defaultConfig;
		return {
			...(filterConfig || {}),
			filters: advancedFilterConfig.filter(Boolean),
		};
	}, [quoteFilters, userType, currentUser, filterConfig]);

	let actions = (
		userType === 'buyer'
			? QUOTE_MULTI_SELECT_ACTION_CONFIG_FOR_BUYER
			: QUOTE_MULTI_SELECT_ACTION_CONFIG_FOR_SUPPLIER
	).map((action) => {
		return {
			...action,
			fn: () =>
				updateQuoteStatuses(
					quotes.selections.map((record) => record.id),
					action.toStatus,
					Object.keys(quotes.selections[0])
				)
					.then((data) => {
						message.destroy('quoteTableMessageUpdating');
						message.open({
							key: 'quoteTableSuccess',
							type: 'success',
							content: data.length.toString().concat(' records updated'),
							duration: 2,
						});
					})
					.catch((error) => {
						message.open({
							key: 'quoteTableError',
							type: 'error',
							content: error,
							duration: 10,
						});
					}),
		};
	});

	let selectItems = enableMultiSelect
		? {
				onSelectCurrentPage: (records) => {
					let keys = unionWith(
						quotes.selections,
						records.map((rec) => getSelectionObject(rec)),
						isEqual
					);
					updateSelections(targetCollectionName, keys);
				},
				onDeselectCurrentPage: (records) => {
					let keys = differenceWith(
						quotes.selections,
						records.map((rec) => getSelectionObject(rec)),
						isEqual
					);
					updateSelections(targetCollectionName, keys);
				},
				onSelectNone: (e) => {
					updateSelections(targetCollectionName, []);
				},
				selections: quotes.selections,
				rowSelection: {
					onSelect: (record, selected, selectedRows, e) => {
						let isSelected = quotes.selections
							.map((item) => item.id)
							.includes(record.id.toString());
						let keys = !isSelected
							? unionWith(quotes.selections, [getSelectionObject(record)], isEqual)
							: differenceWith(quotes.selections, [getSelectionObject(record)], isEqual);
						updateSelections(targetCollectionName, keys);
					},
					selectedRowKeys: quotes.selections.map((item) => item.id),
					preserveSelectedRowKeys: true,
				},
		  }
		: null;

	return (
		<div>
			{CustomHeader ? (
				<CustomHeader
					applySorting={updateSorting}
					applyFilters={(e) => updateFilters(e)}
					filters={activeFilters}
					sorters={activeSorters}
					downloadCSV={() => downloadQuotesCSV({}, activeFilters)}
					counts={quoteSlice.counts}
					filterConfig={FILTER_CONFIG}
					clearFilters={clearFilters}
					targetCollectionName={targetCollectionName}
					userType={userType}
					pagination={pagination}
				/>
			) : (
				<TableHeader
					applySorting={updateSorting}
					applyFilters={(e) => updateFilters(e)}
					filters={activeFilters}
					sorters={activeSorters}
					downloadCSV={() => downloadQuotesCSV({}, activeFilters)}
					counts={quoteSlice.counts}
					filterConfig={FILTER_CONFIG}
					clearFilters={clearFilters}
					targetCollectionName={targetCollectionName}
					enableViews={enableViews}
					userType={userType}
					applyFiltersFromView={replaceFiltersAndSorting}
					preAppliedFilters={preAppliedFilters}
					location={location}
				/>
			)}

			<div style={{ marginTop: 16, marginLeft: 4, background: 'white' }}>
				{enableBannerMessages &&
				nullSafeGet('filters.statuses', quoteSlice[targetCollectionName]) === 'Draft' ? (
					<Row>
						<Alert
							style={{ width: '100%', padding: '8px 9px', borderWidth: 0 }}
							type={'warning'}
							showIcon
							message={
								<span style={{ paddingLeft: '16px' }}>
									Drafted quotes can't be seen by your customers. Please make sure to submit your
									quote once work has been completed.
								</span>
							}
						/>
					</Row>
				) : null}
				{enableMultiSelect ? (
					<TableSelectionRow
						selectItems={selectItems}
						actions={actions}
						records={data}
						counts={quoteSlice.counts}
						updatePagination={updatePagination}
						pagination={pagination}
						updating={updating}
						errors={errors}
					/>
				) : null}
				<QuotesListTable
					fetching={fetching}
					data={data}
					currentUser={currentUser}
					companyConfig={companyConfig}
					renderQuote={renderQuote}
					refreshTable={fetchData}
					tableStyle={tableStyle}
					renderEmptyState={renderEmptyState}
					location={location}
					userType={userType}
					selectItems={selectItems}
					enableMultiSelect={enableMultiSelect}
					backText={backText}
					targetCollectionName={targetCollectionName}
				/>
				{CustomFooter ? (
					<CustomFooter
						updatePagination={updatePagination}
						pagination={{ ...pagination, current: 1, pageSize: 3 }}
						filters={activeFilters}
						sorter={activeSorters}
						location={location}
						history={history}
						enableMultiSelect={enableMultiSelect}
						openMoreInDefault={openMoreInDefault}
						userType={userType}
					/>
				) : (
					<Row justify="end">
						<TableFooter
							records={data}
							//enableMultiSelect={enableMultiSelect}
							updatePagination={updatePagination}
							pagination={
								pagination && pagination.total && pagination.total !== 0 ? pagination : null
							}
							//selectItems={selectItems}
						/>
					</Row>
				)}
			</div>
		</div>
	);
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	handleQueryParams: ownProps.handleQueryParams,
	locations: state.locations,
	errors: state.session.errors,
	loading: state.session.loading,
	spendCategories: state.spend_categories,
	problemTypes: state.problem_types,
	regions: state.regions,
	areas: state.areas,
	quotes: state.proposals[ownProps.targetCollectionName || TARGETCOLLECTIONNAME],
	quoteSlice: state.proposals,
	companyConfig: state.company_config.detail,
	targetCollectionName: ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
	data: getEntitiesById(
		state.proposals[ownProps.targetCollectionName || TARGETCOLLECTIONNAME]
			? state.proposals[ownProps.targetCollectionName || TARGETCOLLECTIONNAME].recordsIds || []
			: [],
		state.proposals.records
	),
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchData: (location) =>
		dispatch(
			fetchQuotes(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				location,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				ownProps.preAppliedFilters,
				ownProps.initialPagination,
				ownProps.initialSorting,
				ownProps.initialFilters,
				ownProps.ignoreRefreshCounts
			)
		),
	updatePagination: (pagination) =>
		dispatch(
			updatePaginationQuotes(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				pagination,
				ownProps.ignoreRefreshCounts
			)
		),
	updateSorting: (sorting) =>
		dispatch(
			updateSortingQuotes(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				sorting,
				ownProps.ignoreRefreshCounts
			)
		),
	updateFilters: (filters) =>
		dispatch(
			updateFiltersQuotes(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				filters,
				ownProps.ignoreRefreshCounts
			)
		),
	updateSelections: (targetCollectionName, selections) =>
		dispatch(
			updateSelection(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				selections
			)
		),
	replaceFiltersAndSorting: (filters, sorting) =>
		dispatch(
			replaceFiltersAndSortingQuotes(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				filters,
				sorting,
				ownProps.ignoreRefreshCounts
			)
		),
	clearFilters: () =>
		dispatch(
			clearFiltersQuotes(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				ownProps.preAppliedFilters,
				ownProps.ignoreRefreshCounts
			)
		),
	updateQuoteStatuses: (ids, status, quoteParams) =>
		dispatch(
			updateQuoteStatusBulkWrapper(ownProps.userType)(
				ids,
				status,
				TARGETCOLLECTIONNAME,
				quoteParams
			)
		),
	downloadQuotesCSV: (params, filters) =>
		dispatch(
			downloadQuoteCSV(ownProps.userType, isSubcontractorQuoteViewLocation(ownProps.location))(
				params,
				filters
			)
		),
});

const QuotesHOC = withRouter(connect(mapStateToProps, mapDispatchToProps)(QuotesTable));

export default connect(
	(state) => ({
		userType: state.session.userType,
		currentUser: state.session.currentUser,
	}),
	(dispatch, ownProps) => ({})
)(QuotesHOC);
